/* ============================================================
   Trust Marquee — Animation Controller
   ============================================================
   SMOOTH  — CSS keyframe animation. JS measures the exact pixel
             period to prevent loop-reset glitches.

   STOP-MOTION — JS center-focus carousel.
             Each item slides smoothly into viewport center,
             holds, then slides out as the next arrives.

             KEY FIX: carousel always starts at the MIDDLE
             duplicate set, so items exist on BOTH sides of
             the initial position in the DOM. Starting at set 0
             left the left edge of the viewport empty.
   ============================================================ */
(function () {
  'use strict';

  var BREAKPOINT = 749;

  function isMobile() {
    return window.innerWidth <= BREAKPOINT;
  }

  function getAnimMode(track) {
    return isMobile() ? track.dataset.animMobile : track.dataset.animDesktop;
  }

  /* ─── Smooth: exact pixel period ──────────────────────────── */

  function measurePeriod(track, copies) {
    var gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
    var period = (track.scrollWidth + gap) / copies;
    track.style.setProperty('--tm-period-px', period + 'px');
  }

  /* ─── Stop-motion: center-focus carousel ──────────────────── */

  var instances = new WeakMap();

  function destroyStopMotion(wrapper) {
    var inst = instances.get(wrapper);
    if (!inst) return;
    inst.destroy();
    instances.delete(wrapper);
  }

  function createStopMotion(wrapper) {
    destroyStopMotion(wrapper);

    var track = wrapper.querySelector('.tm-track');
    var vp    = wrapper.querySelector('.tm-viewport');
    if (!track || !vp) return;

    var allItems  = Array.from(track.querySelectorAll('.tm-item'));
    var N         = allItems.filter(function (i) {
      return !i.hasAttribute('aria-hidden');
    }).length;

    if (N < 1) return;

    var totalCopies = Math.round(allItems.length / N);

    /* Stop-motion needs at least 3 copies:
       one set LEFT of the start position,
       one set to play through (mid),
       one set RIGHT for the seamless wrap clone. */
    if (totalCopies < 3) {
      console.warn(
        '[Trust Marquee] Stop motion needs "Track duplicate copies" ≥ 3. ' +
        'Currently: ' + totalCopies + '.'
      );
      return;
    }

    /* Kill CSS animation; hide until first item is silently positioned. */
    track.classList.add('tm-stop-mode');
    track.style.animation = 'none';
    track.classList.remove('tm-stop-mode-ready');

    var dir = (track.dataset.direction === 'right') ? -1 : 1;

    /* ── Mid-set start ─────────────────────────────────────────
       With copies = 3 → sets [A][B][C], allItems = [A0..AN-1, B0..BN-1, C0..CN-1]
       midSetStart = floor(3/2) × N = N  →  B0 = allItems[N]
       Set A fills the LEFT of the viewport; set C fills the RIGHT.
       No empty edges possible on either side.                   */
    var midSetStart = Math.floor(totalCopies / 2) * N;
    var domIndex    = (dir === 1) ? midSetStart : (midSetStart + N - 1);

    var timer     = null;
    var isPaused  = false;
    var destroyed = false;

    function getPause() {
      var bp = isMobile() ? 'm' : 'd';
      return (
        parseFloat(window.getComputedStyle(wrapper).getPropertyValue('--tm-stop-pause-' + bp)) || 2
      ) * 1000;
    }

    function getTransDur() {
      var bp = isMobile() ? 'm' : 'd';
      return (
        parseFloat(window.getComputedStyle(wrapper).getPropertyValue('--tm-stop-trans-' + bp)) || 0.6
      ) * 1000;
    }

    /* item.offsetLeft is the layout position relative to .tm-track
       (its offsetParent, because track has position:relative).
       CSS transforms do not affect offsetLeft — always stable.    */
    function getTargetTx(item) {
      return vp.offsetWidth / 2 - item.offsetLeft - item.offsetWidth / 2;
    }

    function centerOn(item, animate, onDone) {
      var dur = animate ? getTransDur() : 0;

      if (!animate) {
        track.style.transition = 'none';
        /* Force reflow so the browser flushes 'none' before the
           next transform, preventing accidental animation on snaps. */
        void track.offsetHeight;
      } else {
        track.style.transition =
          'transform ' + (dur / 1000).toFixed(3) + 's ease-in-out';
      }

      track.style.transform = 'translateX(' + getTargetTx(item) + 'px)';

      if (onDone) {
        if (animate) {
          timer = setTimeout(onDone, dur);
        } else {
          requestAnimationFrame(onDone);
        }
      }
    }

    function scheduleAdvance() {
      if (destroyed || isPaused || N <= 1) return;
      timer = setTimeout(advance, getPause());
    }

    function advance() {
      if (destroyed || isPaused) return;

      var nextDom = domIndex + dir;

      /* Forward wrap — about to step into the set AFTER mid.
         Animate to that clone (identical visually to midSetStart),
         then snap invisibly back to midSetStart.                   */
      var fwdWrap = (dir === 1  && nextDom >= midSetStart + N);

      /* Backward wrap — about to step into the set BEFORE mid.
         Animate to that clone (identical visually to midSetStart+N-1),
         then snap invisibly back to midSetStart+N-1.                */
      var bwdWrap = (dir === -1 && nextDom < midSetStart);

      if (fwdWrap) {
        var clone = allItems[nextDom];
        if (!clone) {
          /* Safety: not enough copies; hard-reset to mid start. */
          domIndex = midSetStart;
          centerOn(allItems[domIndex], false, scheduleAdvance);
          return;
        }
        /* 1. Slide to clone in the next set (same visual, different DOM position). */
        centerOn(clone, true, function () {
          if (destroyed) return;
          /* 2. Invisible snap back to the real mid-set equivalent. */
          domIndex = midSetStart;
          centerOn(allItems[domIndex], false, function () {
            if (!destroyed) scheduleAdvance();
          });
        });

      } else if (bwdWrap) {
        var clone = allItems[nextDom];
        /* 1. Slide to clone in the previous set. */
        centerOn(clone, true, function () {
          if (destroyed) return;
          /* 2. Invisible snap back to the real mid-set equivalent. */
          domIndex = midSetStart + N - 1;
          centerOn(allItems[domIndex], false, function () {
            if (!destroyed) scheduleAdvance();
          });
        });

      } else {
        /* Normal advance — no wrap needed. */
        domIndex = nextDom;
        centerOn(allItems[domIndex], true, scheduleAdvance);
      }
    }

    /* Hover pause */
    var pauseOnHover = vp.classList.contains('marquee-pause-on-hover');
    function onEnter() {
      isPaused = true;
      clearTimeout(timer);
      /* Don't interrupt an in-progress slide — let it complete. */
    }
    function onLeave() {
      isPaused = false;
      scheduleAdvance();
    }
    if (pauseOnHover) {
      vp.addEventListener('mouseenter', onEnter);
      vp.addEventListener('mouseleave', onLeave);
    }

    /* Silently position the first item, then reveal and start. */
    centerOn(allItems[domIndex], false, function () {
      if (destroyed) return;
      track.classList.add('tm-stop-mode-ready');
      scheduleAdvance();
    });

    instances.set(wrapper, {
      recenter: function () {
        /* On resize: re-center the current item without restarting.
           Uses domIndex directly — no flickering, no reset to item 0. */
        if (destroyed) return;
        clearTimeout(timer);
        centerOn(allItems[domIndex], false, function () {
          if (!destroyed && !isPaused) scheduleAdvance();
        });
      },
      destroy: function () {
        destroyed = true;
        clearTimeout(timer);
        if (pauseOnHover) {
          vp.removeEventListener('mouseenter', onEnter);
          vp.removeEventListener('mouseleave', onLeave);
        }
        track.classList.remove('tm-stop-mode', 'tm-stop-mode-ready');
        track.style.animation  = '';
        track.style.transform  = '';
        track.style.transition = '';
      }
    });
  }

  /* ─── Per-wrapper orchestration ───────────────────────────── */

  function setupMarquee(wrapper) {
    var track = wrapper.querySelector('.tm-track');
    if (!track) return;

    var copies      = parseInt(wrapper.style.getPropertyValue('--marquee-copies'), 10) || 2;
    var currentMode = null;

    function applyMode() {
      var mode = getAnimMode(track);
      if (mode === currentMode) return;

      if (currentMode === 'smooth') {
        track.classList.remove('tm-ready');
        track.style.transform  = '';
        track.style.transition = '';
      }
      if (currentMode === 'stop_motion') {
        destroyStopMotion(wrapper);
      }

      currentMode = mode;

      if (mode === 'smooth') {
        measurePeriod(track, copies);
        requestAnimationFrame(function () {
          if (currentMode === 'smooth') track.classList.add('tm-ready');
        });
      } else if (mode === 'stop_motion') {
        createStopMotion(wrapper);
      }
    }

    applyMode();

    /* Switch animation mode when viewport crosses the breakpoint. */
    var mq = window.matchMedia('(max-width: ' + BREAKPOINT + 'px)');
    var onMqChange = function () { applyMode(); };
    if (mq.addEventListener) {
      mq.addEventListener('change', onMqChange);
    } else {
      mq.addListener(onMqChange); /* Safari < 14 */
    }

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        if (currentMode === 'smooth') {
          measurePeriod(track, copies);
        } else if (currentMode === 'stop_motion') {
          var inst = instances.get(wrapper);
          if (inst) inst.recenter();
        }
      });
      ro.observe(wrapper);
    }
  }

  function init() {
    document.querySelectorAll('.tm-wrapper').forEach(setupMarquee);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        document.querySelectorAll('.tm-wrapper').forEach(function (wrapper) {
          var track = wrapper.querySelector('.tm-track');
          if (!track || getAnimMode(track) !== 'smooth') return;
          var copies = parseInt(wrapper.style.getPropertyValue('--marquee-copies'), 10) || 2;
          measurePeriod(track, copies);
        });
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();